import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Loader2, Leaf, Users, Scale, Info,
  Check, HelpCircle
} from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';

// Materiality topics - same as backend
const MATERIALITY_TOPICS = {
  environmental: [
    { id: 'climate_change', name: 'Climate Change & GHG Emissions' },
    { id: 'energy_management', name: 'Energy Management' },
    { id: 'water_management', name: 'Water & Effluents' },
    { id: 'waste_management', name: 'Waste & Circular Economy' },
    { id: 'biodiversity', name: 'Biodiversity & Land Use' },
    { id: 'pollution', name: 'Pollution Prevention' },
    { id: 'resource_use', name: 'Resource Use & Efficiency' }
  ],
  social: [
    { id: 'employee_health_safety', name: 'Employee Health & Safety' },
    { id: 'diversity_inclusion', name: 'Diversity & Inclusion' },
    { id: 'labour_practices', name: 'Labour Practices & Rights' },
    { id: 'training_development', name: 'Training & Development' },
    { id: 'community_engagement', name: 'Community Relations' },
    { id: 'customer_welfare', name: 'Customer Welfare' },
    { id: 'supply_chain_social', name: 'Supply Chain Social Standards' },
    { id: 'data_privacy', name: 'Data Privacy & Security' }
  ],
  governance: [
    { id: 'board_composition', name: 'Board Composition & Diversity' },
    { id: 'executive_compensation', name: 'Executive Compensation' },
    { id: 'business_ethics', name: 'Business Ethics & Anti-corruption' },
    { id: 'risk_management', name: 'Risk Management' },
    { id: 'regulatory_compliance', name: 'Regulatory Compliance' },
    { id: 'shareholder_rights', name: 'Shareholder Rights' },
    { id: 'transparency', name: 'Transparency & Disclosure' }
  ]
};

const CATEGORY_ICONS = {
  environmental: { icon: Leaf, color: 'text-green-600 bg-green-50', label: 'Environmental' },
  social: { icon: Users, color: 'text-blue-600 bg-blue-50', label: 'Social' },
  governance: { icon: Scale, color: 'text-purple-600 bg-purple-50', label: 'Governance' }
};

const MaterialityPage = () => {
  const navigate = useNavigate();
  const { currentOrg, user, userData } = useOutletContext();
  const currentUserId = userData?.id;
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('environmental');
  const [editingTopic, setEditingTopic] = useState(null);
  const [ratings, setRatings] = useState({});

  // Fetch assessments
  const fetchData = useCallback(async () => {
    if (!currentOrg) return;
    try {
      setLoading(true);

      // Fetch existing assessments
      const { data: assessmentsData, error } = await supabase
        .from('materiality_assessments')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('materiality_score', { ascending: false });

      if (error) throw error;

      setAssessments(assessmentsData || []);

      // Initialize ratings from existing assessments
      const existingRatings = {};
      (assessmentsData || []).forEach(a => {
        existingRatings[a.topic] = {
          stakeholder_importance: a.stakeholder_importance,
          business_impact: a.business_impact,
          notes: a.notes || ''
        };
      });
      setRatings(existingRatings);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load materiality data');
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRatingChange = (topicId, field, value) => {
    setRatings(prev => ({
      ...prev,
      [topicId]: {
        ...prev[topicId],
        [field]: value
      }
    }));
  };

  const handleSaveRating = async (topicId, category) => {
    const rating = ratings[topicId];
    if (!rating?.stakeholder_importance || !rating?.business_impact) {
      toast.error('Please rate both stakeholder importance and business impact');
      return;
    }
    if (!currentOrg) {
      toast.error('Organisation not loaded');
      return;
    }

    setSaving(true);
    try {
      // Calculate materiality score (geometric mean)
      const materialityScore = Math.sqrt(rating.stakeholder_importance * rating.business_impact);
      const isMaterial = materialityScore >= 3.5;

      // Check if assessment already exists
      const existingAssessment = assessments.find(a => a.topic === topicId);

      if (existingAssessment) {
        // Update existing
        const { error } = await supabase
          .from('materiality_assessments')
          .update({
            stakeholder_importance: rating.stakeholder_importance,
            business_impact: rating.business_impact,
            materiality_score: materialityScore,
            is_material: isMaterial,
            notes: rating.notes || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingAssessment.id);

        if (error) throw error;

        // Log to audit
        await supabase.from('audit_logs').insert({
          id: crypto.randomUUID(),
          user_id: currentUserId,
          organisation_id: currentOrg.id,
          resource_type: 'materiality_assessment',
          resource_id: existingAssessment.id,
          action: 'update',
          description: `Updated materiality assessment for ${topicId}`
        });
      } else {
        // Create new
        const newId = crypto.randomUUID();
        const { error } = await supabase
          .from('materiality_assessments')
          .insert({
            id: newId,
            organisation_id: currentOrg.id,
            topic: topicId,
            category: category,
            stakeholder_importance: rating.stakeholder_importance,
            business_impact: rating.business_impact,
            materiality_score: materialityScore,
            is_material: isMaterial,
            notes: rating.notes || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;

        // Log to audit
        await supabase.from('audit_logs').insert({
          id: crypto.randomUUID(),
          user_id: currentUserId,
          organisation_id: currentOrg.id,
          resource_type: 'materiality_assessment',
          resource_id: newId,
          action: 'create',
          description: `Created materiality assessment for ${topicId}`
        });
      }

      toast.success('Assessment saved');
      setEditingTopic(null);
      fetchData();
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const getTopicName = (topicId) => {
    for (const category of Object.values(MATERIALITY_TOPICS)) {
      const topic = category.find(t => t.id === topicId);
      if (topic) return topic.name;
    }
    return topicId;
  };

  // Prepare data for materiality matrix
  const matrixData = assessments.map(a => ({
    x: a.business_impact,
    y: a.stakeholder_importance,
    topic: getTopicName(a.topic),
    category: a.category,
    is_material: a.is_material,
    score: a.materiality_score
  }));

  const materialTopics = assessments.filter(a => a.is_material);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]">
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Scale className="w-7 h-7 mr-3 text-[#74A57F]" />
              Materiality Assessment
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Identify and prioritise ESG topics for your organisation
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 mb-8">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800">
                <strong>What is Materiality?</strong> A materiality assessment identifies ESG topics that are most
                significant to your business and stakeholders. Topics in the upper-right quadrant (high stakeholder
                importance AND high business impact) are considered "material" and should be prioritized in your
                sustainability reporting and strategy.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Materiality Matrix */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <h2 className="text-lg font-semibold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Materiality Matrix
            </h2>

            {matrixData.length === 0 ? (
              <div className="h-80 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <Scale className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p>Rate topics to see your materiality matrix</p>
                </div>
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      type="number"
                      dataKey="x"
                      domain={[0, 6]}
                      ticks={[1, 2, 3, 4, 5]}
                      label={{ value: 'Business Impact', position: 'bottom', offset: 20 }}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      domain={[0, 6]}
                      ticks={[1, 2, 3, 4, 5]}
                      label={{ value: 'Stakeholder Importance', angle: -90, position: 'left', offset: 10 }}
                      tick={{ fontSize: 12 }}
                    />
                    <ReferenceLine x={3.5} stroke="#94a3b8" strokeDasharray="5 5" />
                    <ReferenceLine y={3.5} stroke="#94a3b8" strokeDasharray="5 5" />
                    <Tooltip
                      content={({ payload }) => {
                        if (!payload || !payload[0]) return null;
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
                            <p className="font-semibold text-[#003B5C]">{data.topic}</p>
                            <p className="text-xs text-slate-500 capitalize">{data.category}</p>
                            <div className="mt-2 text-xs space-y-1">
                              <p>Stakeholder: {data.y}/5</p>
                              <p>Business: {data.x}/5</p>
                              <p className={data.is_material ? 'text-green-600 font-medium' : 'text-slate-500'}>
                                {data.is_material ? '✓ Material' : 'Not Material'}
                              </p>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Scatter data={matrixData}>
                      {matrixData.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={
                            entry.category === 'environmental' ? '#16a34a' :
                              entry.category === 'social' ? '#2563eb' :
                                '#9333ea'
                          }
                          opacity={entry.is_material ? 1 : 0.5}
                        />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 justify-center text-xs">
              {Object.entries(CATEGORY_ICONS).map(([key, { label }]) => (
                <div key={key} className="flex items-center space-x-1">
                  <div className={`w-3 h-3 rounded-full ${key === 'environmental' ? 'bg-green-500' :
                    key === 'social' ? 'bg-blue-500' :
                      'bg-purple-500'
                    }`} />
                  <span className="text-slate-600">{label}</span>
                </div>
              ))}
            </div>

            {/* Material Topics Summary */}
            {materialTopics.length > 0 && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-100">
                <h4 className="font-medium text-green-800 mb-2">
                  Material Topics ({materialTopics.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {materialTopics.map(t => (
                    <span key={t.id} className="px-2 py-1 bg-white rounded text-xs text-green-700 border border-green-200">
                      {getTopicName(t.topic)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Topic Rating */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Rate ESG Topics
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Rate each topic on stakeholder importance and business impact (1-5)
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex border-b border-slate-100">
              {Object.entries(CATEGORY_ICONS).map(([key, { icon: Icon, label }]) => (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`flex-1 px-4 py-3 flex items-center justify-center space-x-2 text-sm font-medium transition-colors ${activeCategory === key
                    ? 'text-[#003B5C] border-b-2 border-[#003B5C] bg-slate-50'
                    : 'text-slate-500 hover:text-slate-700'
                    }`}
                  data-testid={`tab-${key}`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {/* Topics List */}
            <div className="max-h-[500px] overflow-y-auto">
              {MATERIALITY_TOPICS[activeCategory]?.map(topic => {
                const rating = ratings[topic.id] || {};
                const isAssessed = assessments.some(a => a.topic === topic.id);
                const assessment = assessments.find(a => a.topic === topic.id);

                return (
                  <div
                    key={topic.id}
                    className={`p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors ${editingTopic === topic.id ? 'bg-blue-50' : ''
                      }`}
                    data-testid={`topic-${topic.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-slate-800">{topic.name}</span>
                          {isAssessed && (
                            <span className={`px-2 py-0.5 rounded text-xs ${assessment?.is_material
                              ? 'bg-green-100 text-green-700'
                              : 'bg-slate-100 text-slate-600'
                              }`}>
                              {assessment?.is_material ? 'Material' : 'Assessed'}
                            </span>
                          )}
                        </div>

                        {editingTopic === topic.id ? (
                          <div className="mt-3 space-y-4">
                            {/* Stakeholder Importance */}
                            <div>
                              <Label className="text-xs text-slate-600 mb-2 block">
                                Stakeholder Importance
                                <HelpCircle className="w-3 h-3 inline ml-1 text-slate-400" />
                              </Label>
                              <div className="flex space-x-2">
                                {[1, 2, 3, 4, 5].map(n => (
                                  <button
                                    key={n}
                                    onClick={() => handleRatingChange(topic.id, 'stakeholder_importance', n)}
                                    className={`w-10 h-10 rounded-lg border-2 font-medium transition-all ${rating.stakeholder_importance === n
                                      ? 'border-[#003B5C] bg-[#003B5C] text-white'
                                      : 'border-slate-200 hover:border-slate-300'
                                      }`}
                                    data-testid={`stakeholder-${n}`}
                                  >
                                    {n}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Business Impact */}
                            <div>
                              <Label className="text-xs text-slate-600 mb-2 block">
                                Business Impact
                                <HelpCircle className="w-3 h-3 inline ml-1 text-slate-400" />
                              </Label>
                              <div className="flex space-x-2">
                                {[1, 2, 3, 4, 5].map(n => (
                                  <button
                                    key={n}
                                    onClick={() => handleRatingChange(topic.id, 'business_impact', n)}
                                    className={`w-10 h-10 rounded-lg border-2 font-medium transition-all ${rating.business_impact === n
                                      ? 'border-[#003B5C] bg-[#003B5C] text-white'
                                      : 'border-slate-200 hover:border-slate-300'
                                      }`}
                                    data-testid={`business-${n}`}
                                  >
                                    {n}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Notes */}
                            <div>
                              <Label className="text-xs text-slate-600 mb-1 block">Notes (optional)</Label>
                              <Textarea
                                value={rating.notes || ''}
                                onChange={(e) => handleRatingChange(topic.id, 'notes', e.target.value)}
                                placeholder="Any additional context..."
                                rows={2}
                                className="text-sm"
                                data-testid="notes-textarea"
                              />
                            </div>

                            {/* Actions */}
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                onClick={() => handleSaveRating(topic.id, activeCategory)}
                                disabled={saving}
                                className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                                data-testid="save-rating-btn"
                              >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingTopic(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 flex items-center space-x-4">
                            {isAssessed ? (
                              <>
                                <span className="text-xs text-slate-500">
                                  S: {assessment?.stakeholder_importance}/5 | B: {assessment?.business_impact}/5
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingTopic(topic.id)}
                                  className="text-xs text-[#003B5C]"
                                  data-testid={`edit-${topic.id}`}
                                >
                                  Edit
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingTopic(topic.id)}
                                className="text-xs"
                                data-testid={`rate-${topic.id}`}
                              >
                                Rate Topic
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MaterialityPage;
